import { Download, Visibility, InsertDriveFile } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useGetLocale, useModal } from "@refinedev/core";
import { useMemo, useState } from "react";
import { ReturnLabel } from "../../types";
import { useTranslation } from "../../../../hooks/useTranslation";
import { ReturnLabelPreviewModal } from "./ReturnLabelPreviewModal";

type Props = {
  labels: ReturnLabel[];
};

const formatDate = (value: string | undefined, locale: string | undefined) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString(locale);
};

export const ReturnLabels = ({ labels }: Props) => {
  const { translate } = useTranslation("pages.orderDetails.returnLabels");
  const locale = useGetLocale()();
  const previewModal = useModal();
  const [previewLabel, setPreviewLabel] = useState<ReturnLabel | undefined>();

  const sortedLabels = useMemo(
    () => [...labels].sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt)),
    [labels],
  );

  const handlePreview = (label: ReturnLabel) => {
    setPreviewLabel(label);
    previewModal.show();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: (theme) => theme.palette.customBackground.box,
        borderLeft: "3px solid",
        borderColor: "secondary.main",
      }}
    >
      <Typography variant="subtitle1" fontWeight={500}>
        {translate("title")}
      </Typography>

      <Divider sx={{ my: 1 }} />

      {sortedLabels.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {translate("empty")}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {sortedLabels.map((label) => {
            const uploadedAt = formatDate(label.uploadedAt, locale);
            const expiresAt = formatDate(label.expiresAt, locale);

            const dateText = uploadedAt
              ? label.uploadedBy
                ? translate("uploadedByAt", {
                    uploadedBy: label.uploadedBy,
                    uploadedAt,
                  })
                : translate("uploadedAt", { uploadedAt })
              : label.uploadedBy
                ? translate("uploadedBy", { uploadedBy: label.uploadedBy })
                : undefined;

            return (
              <Box
                key={label.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <InsertDriveFile color="action" />

                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    title={label.fileName}
                  >
                    {label.fileName}
                  </Typography>
                  {dateText && (
                    <Typography variant="caption" color="text.secondary">
                      {dateText}
                    </Typography>
                  )}
                  {expiresAt && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {translate("expiresAt", { expiresAt })}
                    </Typography>
                  )}
                </Box>

                <IconButton
                  color="primary"
                  onClick={() => handlePreview(label)}
                  title={translate("preview")}
                >
                  <Visibility fontSize="small" />
                </IconButton>

                <IconButton
                  color="primary"
                  href={label.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={label.fileName}
                  title={translate("download")}
                >
                  <Download fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      )}

      <ReturnLabelPreviewModal
        open={previewModal.visible}
        onClose={previewModal.close}
        label={previewLabel}
      />
    </Paper>
  );
};
